import {
  Avatar,
  Box,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tabs,
  Tab,
  FormControl,
  Autocomplete,
} from '@material-ui/core';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import setTitle from 'src/utils/setTitle';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { Formik } from 'formik';
import toast from 'react-hot-toast';
import CustomButton from 'src/components/widgets/buttons/CustomButton';
import EyeIcon from 'src/icons/Eye';
import EyeOffIcon from 'src/icons/EyeOff';
import logger from 'src/utils/logger';
import * as Yup from 'yup';
import gtm from 'src/lib/gtm';
// import { userApi } from 'src/api/userApi';
import useAuth from 'src/hooks/useAuth';
import { contactsApi } from 'src/api/contactsApi';
import { rolesApi } from 'src/api/rolesApi';
import { useTranslation } from 'react-i18next';
import { REGEX } from 'src/utils/constants';
import { Upload } from '@material-ui/icons';
import SubHeader from 'src/components/SubHeader';

const useStyles = makeStyles<Theme>((theme: Theme) =>
  createStyles({
    pageContainer: {
      backgroundColor: '#F8FAFC',
      minHeight: '100vh',
    },
    headerWrapper: {
      backgroundColor: '#FFFFFF',
      borderBottom: '1px solid #E2E8F0',
    },
    tabsContainer: {
      // maxWidth: '800px',
      margin: '0 auto',
      padding: '10px 10px 0 10px',
    },
    tabs: {
      minHeight: 'auto',
      '& .MuiTabs-indicator': {
        backgroundColor: theme.palette.primary.main,
        height: '3px',
      },
      '& .MuiTabs-flexContainer': {
        justifyContent: 'flex-start',
      },
    },
    tab: {
      textTransform: 'none',
      fontFamily: 'Inter, sans-serif',
      fontSize: '14px',
      fontWeight: '500',
      color: '#64748B',
      minWidth: 'auto',
      padding: '16px 20px',
      '&.Mui-selected': {
        color: theme.palette.primary.main,
      },
    },
    mainContainer: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: '32px 24px',
    },
    profileCard: {
      backgroundColor: '#FFFFFF',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '20px',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
    },
    profileAvatar: {
      width: '70px',
      height: '70px',
    },
    profileInfo: {
      flex: 1,
    },
    profileName: {
      fontSize: '18px',
      fontWeight: 'bold',
      color: '#1E293B',
      marginBottom: '4px',
      fontFamily: 'Inter, sans-serif',
    },
    imageGuidelines: {
      fontSize: '14px',
      color: '#64748B',
      fontFamily: 'Inter, sans-serif',
    },
    uploadButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: '#1E293B',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      textDecoration: 'none',
      fontFamily: 'Inter, sans-serif',
      '&:hover': {
        opacity: 0.8,
      },
    },
    formContainer: {
      borderRadius: '12px',
      padding: '10px 24px',
    },
    sectionTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1E293B',
      marginBottom: '24px',
      fontFamily: 'Inter, sans-serif',
    },
    formGrid: {
      marginBottom: '16px',
    },
    fieldLabel: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#1E293B',
      marginBottom: '8px',
      display: 'block',
      fontFamily: 'Inter, sans-serif',
    },
    textField: {
      '& .MuiOutlinedInput-root': {
        backgroundColor: '#FFFFFF',
        borderRadius: '6px',
        height: '44px',
        '& fieldset': {
          borderColor: '#E2E8F0',
        },
        '&:hover fieldset': {
          borderColor: '#CBD5E1',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#3B82F6',
        },
      },
      '& .MuiInputBase-input': {
        padding: '12px 16px',
        fontSize: '14px',
        fontFamily: 'Inter, sans-serif',
        '&::placeholder': {
          color: '#CBD5E1',
          opacity: 1,
        },
      },
    },
    textAreaField: {
      '& .MuiOutlinedInput-root': {
        backgroundColor: '#FFFFFF',
        borderRadius: '6px',
        '& fieldset': {
          borderColor: '#E2E8F0',
        },
        '&:hover fieldset': {
          borderColor: '#CBD5E1',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#3B82F6',
        },
      },
      '& .MuiInputBase-input': {
        padding: '12px 16px',
        fontSize: '14px',
        fontFamily: 'Inter, sans-serif',
        '&::placeholder': {
          color: '#CBD5E1',
          opacity: 1,
        },
      },
    },
    saveButton: {
      backgroundColor: '#EF4444',
      color: '#FFFFFF',
      fontSize: '16px',
      fontWeight: '600',
      padding: '12px 24px',
      borderRadius: '6px',
      textTransform: 'none',
      fontFamily: 'Inter, sans-serif',
      '&:hover': {
        backgroundColor: '#DC2626',
      },
    },
    loadingCircle: {
      color: theme.palette.primary.main,
      width: '40px !important',
      height: '40px !important',
      marginLeft: 5,
      marginRight: 5,
    },
    loadingWrapper: {
      textAlign: 'center',
    },
  })
);

const Account: FC = () => {
  setTitle('ITSM | Account');
  const { t } = useTranslation('accountInfo');
  const classes = useStyles();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [roles, setRoles] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const { user } = useAuth();

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setActiveTab(newValue);
  };

  const handlePicture = async (e) => {
    try {
      const file = e.target.files[0];

      if (!file) {
        return null;
      }

      // Check file type
      const fileType = file.type;
      if (!['image/jpeg', 'image/png'].includes(fileType)) {
        // toast.dismiss();
        toast.error('Invalid File Type. Only PNG and JPG are allowed');
        setIsSubmitting(false);
        return null;
      }

      // Check file size (10MB = 10 * 1024 * 1024 bytes)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        // toast.dismiss();
        toast.error('Max file size is 10MB', {
          duration: 6000,
        });
        setIsSubmitting(false);
        return null;
      }

      setIsSubmitting(true);

      // Create FormData and upload using the same approach as CustomDialog
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${process.env.REACT_APP_IMAGE_UPLOAD_URL}/upload`, {
        method: 'POST',
        headers: {
          'Access-Control-Allow-Origin': '*',
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();

      setIsSubmitting(false);
      toast.dismiss();
      toast.success('Image uploaded successfully', {
        duration: 10000,
      });

      // Return the blob_url to match CustomDialog's behavior
      return data.blob_url || data.fileUrl;

    } catch (err) {
      setIsSubmitting(false);
      logger(err, 'error');
      toast.dismiss();
      toast.error('Failed to upload image. Please try again.', {
        duration: 10000,
      });
      return null;
    }
  };
  const hasFormChanges = (initialValues, currentValues) => {
    const fieldsToCompare = [
      'firstName',
      'lastName',
      'designation',
      'email',
      'phoneNumber',
      'image',
      'description',
    ];

    const hasFieldChanges = fieldsToCompare.some(
      (field) => initialValues[field] !== currentValues[field]
    );

    if (hasFieldChanges) {
      return true;
    }

    if (
      currentValues.oldPassword?.trim() ||
      currentValues.newPassword?.trim() ||
      currentValues.confirmNewPassword?.trim()
    ) {
      return true;
    }

    return false;
  };
  const getAllRoles = async () => {
    try {
      const response = await rolesApi.getAllActiveRoles();
      if (response?.status === 200) {
        setRoles(response?.data);
      } else {
        setRoles([]);
      }
    } catch (error) {
      setRoles([]);
    }
  };

  const [showPassword, setShowPassword] = useState<Array<boolean>>([
    false,
    false,
    false,
  ]);

  const handleClickShowPassword = (psType: number) => {
    setShowPassword((prev) => {
      const arrCopy = [...showPassword];
      arrCopy[psType] = !prev[psType];
      return arrCopy;
    });
  };

  const INITIAL_VALUES = {
    name: '',
    firstName: '',
    lastName: '',
    designation: '',
    roles: [],
    email: '',
    contactNumber: '',
    avatar: '',
    info: '',
  };

  const [employeeData, setEmployeeData] = useState(INITIAL_VALUES);

  const handleOnSubmit = async (values: any, initialValues: any) => {
    try {
      if (!hasFormChanges(initialValues, values)) {
        toast(
          "ℹ️ No changes to save",
          {
            duration: 6000,
          }
        );
        return;
      }
      const filter = {
        name: values?.userName,
        firstName: values?.firstName,
        lastName: values?.lastName,
        email: values?.email,
        avatar: values?.image,
        info: values?.description,
        contactNumber: values?.phoneNumber,
        oldPassword: values?.oldPassword.trim(),
        password: values?.confirmNewPassword.trim(),
        designation: values?.designation.trim(),
      };
      const response = await contactsApi.updateByIdWithPassword(filter);
      if (response?.status === 200) {
        toast.success('Updated Successfully');
      }
    } catch (error) {
      if (error?.response?.status === 409) {
        toast.error(`${error?.response?.data?.message}`);
      } else if (error?.response?.status === 400) {
        toast.error(error?.response?.data?.message);
      } else if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message);
      }
    }
  };

  const getLoggedInUserData = async () => {
    try {
      const response = await contactsApi.getById(user?.id);
      if (response?.status === 200) {
        setEmployeeData(response?.data);
      }
    } catch (error) {
      logger(error, 'error');
    }
  };

  useEffect(() => {
    getLoggedInUserData();
    getAllRoles();
    gtm.push({ event: 'page_view' });
  }, []);

  return (
    <Box>
      <Box className={classes.headerWrapper}>
        <SubHeader title="Account Settings" />
        <Box className={classes.tabsContainer}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            className={classes.tabs}
          >
            <Tab label="Overview" className={classes.tab} />
            <Tab label="Security" className={classes.tab} />
          </Tabs>
        </Box>
      </Box>

      <Box className={classes.pageContainer}>
        <div className={classes.mainContainer}>
          <Formik
            enableReinitialize
            initialValues={{
              userName: employeeData?.name || '',
              firstName: employeeData?.firstName || '',
              lastName: employeeData?.lastName || '',
              designation: employeeData?.designation || '',
              role: employeeData?.roles || [],
              email: employeeData?.email || '',
              phoneNumber: employeeData?.contactNumber[0] || '',
              image: employeeData?.avatar || '',
              description: employeeData?.info || '',
              oldPassword: '',
              newPassword: '',
              confirmNewPassword: '',
            }}
            onSubmit={(values) => {
              handleOnSubmit(values, {
                userName: employeeData?.name || '',
                firstName: employeeData?.firstName || '',
                lastName: employeeData?.lastName || '',
                designation: employeeData?.designation || '',
                role: employeeData?.roles || [],
                email: employeeData?.email || '',
                phoneNumber: employeeData?.contactNumber[0] || '',
                image: employeeData?.avatar || '',
                description: employeeData?.info || '',
                oldPassword: '',
                newPassword: '',
                confirmNewPassword: '',
              });
            }}
            validationSchema={Yup.object({
              firstName: Yup.string().required(
                t('accountInfo_form_firstNameField_requiredError')
              ),
              lastName: Yup.string().required(
                t('accountInfo_form_lastNameField_requiredError')
              ),
              email: Yup.string()
                .email()
                .required(t('accountInfo_form_emailField_requiredError')),
              role: Yup.array()
                .nullable()
                .test(
                  'is-nonempty',
                  t('accountInfo_form_roleFiel_requiredError'),
                  (value) => value && value?.length > 0
                ),
              image: Yup.string().required(
                t('accountInfo_form_imageField_requiredError')
              ),
              description: Yup.string().max(
                255,
                t('accountInfo_form_bioField_maximumlengthError')
              ),
              oldPassword: Yup.string()
                .trim()
                .when('newPassword', {
                  is: (newPassword) => newPassword && newPassword.length > 0,
                  then: Yup.string().required('Old Password required'),
                  otherwise: Yup.string(),
                }),
              newPassword: Yup.string()
                .trim()
                .min(8, 'Minium 8 characters required')
                .max(20, 'Maximum 20 characters allowed')
                .matches(
                  REGEX.password,
                  'At least one uppercase, one lowercase, one number & one special character allowed'
                ),
              confirmNewPassword: Yup.string()
                .oneOf(
                  [Yup.ref('newPassword'), null],
                  t('accountInfo_form_confirmNewPasswordField_notMatchingError')
                ),
            })}>
            {({
              values,
              errors,
              touched,
              handleChange,
              handleSubmit,
              handleBlur,
              setFieldValue,
            }) => (
              <form noValidate>
                {/* Profile Section - Always visible */}


                {/* Overview Tab */}
                {activeTab === 0 && (
                  <div className={classes.formContainer}>
                    <div className={classes.profileCard}>
                      {isSubmitting ? (
                        <div className={classes.loadingWrapper}>
                          <CircularProgress className={classes.loadingCircle} />
                        </div>
                      ) : (
                        <Avatar
                          className={classes.profileAvatar}
                          src={values?.image || 'https://assets.it22.nl/fortes-energy/admin/dummy_image.png'}
                        />
                      )}
                      <div className={classes.profileInfo}>
                        <div className={classes.profileName}>
                          {values?.firstName && values?.lastName
                            ? `${values.firstName} ${values.lastName}`
                            : 'User Name'}
                        </div>
                        <div className={classes.imageGuidelines}>
                          PNG or JPG no bigger than 10MB.
                        </div>
                      </div>
                      <label htmlFor="image-upload" className={classes.uploadButton}>
                        <Upload />
                        Upload
                        <input
                          id="image-upload"
                          type="file"
                          name="image"
                          onChange={async (e) => {
                            const imageUrl = await handlePicture(e);
                            if (imageUrl) {
                              setFieldValue('image', imageUrl);
                            }
                            // Reset the input so the same file can be uploaded again if needed
                            e.target.value = '';
                          }}
                          accept="image/png, image/jpeg"
                          style={{ display: 'none' }}
                        />
                      </label>
                    </div>
                    <div className={classes.sectionTitle}>Account Information</div>
                    <Grid container spacing={2}>
                      {/* First Row */}
                      <Grid item xs={6}>
                        <label htmlFor='firstName' className={classes.fieldLabel}>
                          {t('accountInfo_form_firstNameField_label')}
                        </label>
                        <TextField
                          fullWidth
                          className={classes.textField}
                          placeholder="First Name"
                          name="firstName"
                          value={values.firstName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(touched?.firstName && errors?.firstName)}
                          helperText={touched?.firstName && errors?.firstName}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <label htmlFor='lastName' className={classes.fieldLabel}>
                          {t('accountInfo_form_lastNameField_label')}
                        </label>
                        <TextField
                          fullWidth
                          id='lastName'
                          className={classes.textField}
                          placeholder="Last Name"
                          name="lastName"
                          value={values.lastName}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(touched?.lastName && errors?.lastName)}
                          helperText={touched?.lastName && errors?.lastName}
                          variant="outlined"
                        />
                      </Grid>

                      {/* Second Row */}
                      <Grid item xs={6}>
                        <label htmlFor='userName' className={classes.fieldLabel}>
                          {t('accountInfo_form_userNameField_label')}
                        </label>
                        <TextField
                          fullWidth
                          disabled
                          id='userName'
                          className={classes.textField}
                          placeholder="User Name"
                          name="userName"
                          value={values?.userName}
                          onBlur={handleBlur}
                          error={Boolean(touched?.userName && errors?.userName)}
                          helperText={touched?.userName && errors?.userName}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <label htmlFor='designationField' className={classes.fieldLabel}>
                          {t('accountInfo_form_designationField_label')}
                        </label>
                        <TextField
                          fullWidth
                          id='designationField'
                          className={classes.textField}
                          placeholder="Designation"
                          name="designation"
                          value={values.designation}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(
                            touched?.designation && errors?.designation
                          )}
                          helperText={touched?.designation && errors?.designation}
                          variant="outlined"
                        />
                      </Grid>

                      {/* Third Row */}
                      <Grid item xs={6}>
                        <label htmlFor='email' className={classes.fieldLabel}>
                          {t('accountInfo_form_emailField_label')}
                        </label>
                        <TextField
                          fullWidth
                          disabled
                          id='email'
                          className={classes.textField}
                          placeholder="Email"
                          name="email"
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(touched?.email && errors?.email)}
                          helperText={touched?.email && errors?.email}
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <label htmlFor='phone' className={classes.fieldLabel}>
                          {t('accountInfo_form_phoneNumberField_label')}
                        </label>
                        <TextField
                          fullWidth
                          id='phone'
                          className={classes.textField}
                          placeholder="Phone Number"
                          name="phoneNumber"
                          value={values.phoneNumber}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(
                            touched?.phoneNumber && errors?.phoneNumber
                          )}
                          helperText={touched?.phoneNumber && errors?.phoneNumber}
                          variant="outlined"
                        />
                      </Grid>

                      {/* Role Field - Full Width */}
                      <Grid item xs={12}>
                        <FormControl
                          disabled
                          fullWidth
                          error={Boolean(errors.role && touched.role)}>
                          <Autocomplete
                            id="role-autocomplete"
                            options={roles}
                            value={values.role}
                            multiple
                            disabled
                            getOptionLabel={(option) =>
                              Object.keys(option).length !== 0 && option?.name
                            }
                            onChange={(e, value) => setFieldValue('role', value)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={t('accountInfo_form_roleField_label')}
                                variant="outlined"
                                name="role"
                                // placeholder="Placeholder"
                                error={Boolean(errors?.role && touched?.role)}
                                helperText={touched?.role && errors?.role}
                                className={classes.textField}
                                InputLabelProps={{
                                  shrink: true,
                                  style: {
                                    position: 'static',
                                    transform: 'none',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    color: '#1E293B',
                                    marginBottom: '8px',
                                    fontFamily: 'Inter, sans-serif',
                                  },
                                }}
                                InputProps={{
                                  ...params.InputProps,
                                  style: {
                                    minHeight: '44px',
                                    height: 'auto',
                                    alignItems: 'flex-start',
                                    paddingTop: '8px',
                                    paddingBottom: '8px',
                                  },
                                }}
                              />
                            )}
                          />
                        </FormControl>
                      </Grid>
                      {/* Bio Field */}
                      <Grid item xs={12}>
                        <label htmlFor='bio' className={classes.fieldLabel}>
                          {t('accountInfo_form_bioField_label')}
                        </label>
                        <TextField
                          fullWidth
                          id='bio'
                          className={classes.textAreaField}
                          placeholder="Bio"
                          name="description"
                          value={values.description}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={Boolean(
                            touched?.description && errors?.description
                          )}
                          helperText={errors?.description}
                          multiline
                          rows={3}
                          variant="outlined"
                        />
                      </Grid>

                      {/* Save Button */}
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
                          <CustomButton
                            value={t('accountInfo_submitButton_label')}
                            className={classes.saveButton}
                            onClick={handleSubmit}
                            type="submit"
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </div>
                )}

                {/* Security Tab */}
                {activeTab === 1 && (
                  <div className={classes.formContainer}>
                    <div className={classes.sectionTitle}>Change Password</div>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <label htmlFor='oldPassword' className={classes.fieldLabel}>
                          {t('accountInfo_form_oldPasswordField_label')}
                        </label>
                        <TextField
                          fullWidth
                          id='oldPassword'
                          className={classes.textField}
                          placeholder="Old Password"
                          name="oldPassword"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(
                            touched.oldPassword && errors.oldPassword
                          )}
                          helperText={touched.oldPassword && errors.oldPassword}
                          type={showPassword[0] ? 'text' : 'password'}
                          value={values?.oldPassword}
                          variant="outlined"
                          InputProps={{
                            readOnly: false,
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={() => handleClickShowPassword(0)}>
                                  {showPassword[0] ? <EyeIcon /> : <EyeOffIcon />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <label htmlFor='newPassword' className={classes.fieldLabel}>
                          {t('accountInfo_form_newPasswordField_label')}
                        </label>
                        <TextField
                          fullWidth
                          id='newPassword'
                          className={classes.textField}
                          placeholder="New Password"
                          name="newPassword"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(
                            touched.newPassword && errors.newPassword
                          )}
                          helperText={touched.newPassword && errors.newPassword}
                          type={showPassword[1] ? 'text' : 'password'}
                          value={values.newPassword}
                          variant="outlined"
                          InputProps={{
                            readOnly: false,
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={() => handleClickShowPassword(1)}>
                                  {showPassword[1] ? <EyeIcon /> : <EyeOffIcon />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <label htmlFor='confirmNewPassword' className={classes.fieldLabel}>
                          {t('accountInfo_form_confirmNewPasswordField_label')}
                        </label>
                        <TextField
                          fullWidth
                          id='confirmNewPassword'
                          className={classes.textField}
                          placeholder="Confirm New Password"
                          name="confirmNewPassword"
                          onBlur={handleBlur}
                          onChange={handleChange}
                          error={Boolean(
                            touched.confirmNewPassword &&
                            errors.confirmNewPassword
                          )}
                          helperText={
                            touched.confirmNewPassword &&
                            errors.confirmNewPassword
                          }
                          type={showPassword[2] ? 'text' : 'password'}
                          value={values?.confirmNewPassword}
                          variant="outlined"
                          InputProps={{
                            readOnly: false,
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={() => handleClickShowPassword(2)}>
                                  {showPassword[2] ? <EyeIcon /> : <EyeOffIcon />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>

                      {/* Save Button */}
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
                          <CustomButton
                            value={t('accountInfo_submitButton_label')}
                            className={classes.saveButton}
                            onClick={handleSubmit}
                            type="submit"
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </div>
                )}
              </form>
            )}
          </Formik>
        </div>
      </Box>
    </Box>
  );
};

export default Account;